import React from 'react';
import { Box, Text } from 'ink';
import { DEFAULT_TERMINAL_WIDTH } from '../../constants';
import { formatDuration } from '../../lib/duration';
import { useElapsedTime } from '../../lib/hooks/use_elapsed_time';
import { ControlsHint } from '../../lib/components/ControlsHint';
import { BaseTool, toolContentWidth } from './BaseTool';
import { truncate, isTruncatable } from './truncate';
import type { ToolComponentProps, DelegateTaskInput } from './types';

export const DelegateTaskTool: React.FC<
  ToolComponentProps<DelegateTaskInput> & { timestamp: number }
> = ({ input, state, expanded, columns = DEFAULT_TERMINAL_WIDTH, timestamp }) => {
  // The ticker exists to show the session is not stuck, so it runs only while the
  // delegation does and no time is shown once there is a result.
  const elapsedMs = useElapsedTime(state.type === 'loading' ? timestamp : undefined);
  const duration = elapsedMs === undefined ? '' : ` (${formatDuration(elapsedMs)})`;

  const target = input.subagentName === undefined ? '' : ` to \`${input.subagentName}\``;
  const label = `Delegated${target}${duration}`;
  const hasSession = input.subagentName !== undefined;

  // BaseTool renders the error variant itself, so only the answer is shown here.
  const output = state.type === 'success' ? state.output.trim() : '';
  const outputTruncatable = isTruncatable(output);
  const displayOutput = expanded ? output : truncate(output);
  const contentWidth = toolContentWidth(columns);

  return (
    <BaseTool
      label={label}
      state={state}
      expanded={expanded}
      columns={columns}
      showExpandHint={outputTruncatable}
    >
      {output.length > 0 && (
        <Box flexDirection="column" paddingLeft={2} width={contentWidth}>
          <Text>{displayOutput}</Text>
          {!expanded && outputTruncatable && <Text dimColor>(truncated)</Text>}
        </Box>
      )}

      {hasSession && (
        <Box paddingLeft={2} width={contentWidth}>
          <ControlsHint>*Ctrl+T* to view its session</ControlsHint>
        </Box>
      )}
    </BaseTool>
  );
};
